import React, { useEffect, useRef, useState } from 'react'
import { Button, Empty, Form, Select, Spin } from 'antd'
import { RelationshipWrapper, CrossDataSettingWrapper, RelationshipRow } from '../dashboard.styled'
import { useTranslation } from 'react-i18next'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import MainTable from '../../../components/Basic/MainTable'
import useParams from '../../../hook/useParams'
import { crossDataFormatSave } from '../Conf'
import { fetchCrossDataPreview } from '../../../apis/dashboard'

const maxCount = 5
function CrossDataSetting (props) {
  const { defPlaceholder, defConfig, form, dataSourceTree, validateCrossData, count, setCount, dataSourceTableMapping, uniqueDataSrcTableKeys, tableJoinKeyMapping, setAnyCrossDataChange, hidePreview, onRelate, uniqueKey, disabled, disabledActions, useJoinLogic, viewOnly } = props
  const { t: ln_t } = useTranslation()
  const relationshipRef = useRef()
  const params = useParams()
  const { handleFetchParams } = params
  const [columns, setColumns] = useState([])
  const [tablePreview, setTablePreview] = useState([])
  const [tablePreviewStatus, setTablePreviewStatus] = useState('blank')
  const [loading, setLoading] = useState(false)
  const formVal = Form.useWatch() || {}

  const dataPreviewApi = async () => ({ status: 200, data: tablePreview })

  const usedDataSrcTable = count?.map(i => {
    const key = `${uniqueKey ? `${uniqueKey}-` : ''}${i}`
    const parent = formVal[`${key}-parent-dataSource`] && formVal[`${key}-parent-tableName`] ? [`${formVal[`${key}-parent-dataSource`]}-${formVal[`${key}-parent-tableName`]}`] : []
    const child = formVal[`${key}-child-dataSource`] && formVal[`${key}-child-tableName`] ? [`${formVal[`${key}-child-dataSource`]}-${formVal[`${key}-child-tableName`]}`] : []
    return [...parent, ...child]
  }).flat()

  const getDataSrcOpt = (key, type) => {
    const sequence = uniqueKey ? Number(key.split('-')?.[1]) : key
    if (type === 'parent' && sequence > 1) {
      const relationshipBefore = count.filter(i => i < sequence).map(i => [formVal[`${uniqueKey ? `${uniqueKey}-` : ''}${i}-parent-dataSource`], formVal[`${uniqueKey ? `${uniqueKey}-` : ''}${i}-child-dataSource`]]).flat()
      return dataSourceTree.filter(i => relationshipBefore.includes(i.dataSourceId))?.map(i => ({ value: i.dataSourceId, label: i.dataSourceName }))
    }
    const availableDataSrc = [...new Set(uniqueDataSrcTableKeys?.filter(i => !usedDataSrcTable.includes(i))?.map(i => i?.split('-')?.[0]))]
    return dataSourceTree?.map(i => ({ value: i.dataSourceId, label: i.dataSourceName, disabled: !availableDataSrc.includes(`${i.dataSourceId}`) }))
  }

  const getTableOpt = (key, type) => {
    const sequence = uniqueKey ? Number(key.split('-')?.[1]) : key
    const selectedDataSource = form.getFieldValue(`${key}-${type}-dataSource`)
    const usedTable = usedDataSrcTable?.filter(i => i.startsWith(`${selectedDataSource}-`))?.map(i => i.replace(`${selectedDataSource}-`, ''))
    const tableOpt = dataSourceTableMapping[selectedDataSource] || []
    return type === 'parent' && sequence > 1 ? tableOpt.filter(i => usedTable.includes(i.value)) : tableOpt.map(i => ({ ...i, disabled: usedTable.includes(i.value) }))
  }

  const getColumnOpt = (sequence, type) => {
    const selectedSrc = form.getFieldValue(`${sequence}-${type}-dataSource`)
    const selectedTable = form.getFieldValue(`${sequence}-${type}-tableName`)
    return tableJoinKeyMapping[`${selectedSrc}-${selectedTable}`] || []
  }

  const resetFields = sequence => ({
    ...fields.map(i => ({ [`${sequence}-parent-${i.key}`]: undefined, [`${sequence}-child-${i.key}`]: undefined })).reduce((all, i) => ({ ...all, ...i }), {}),
    ...useJoinLogic ? { [`${sequence}-logic`]: undefined } : {}
  })

  const resetRelBelow = key => {
    const sequence = uniqueKey ? Number(key.split('-')?.[1]) : key
    return count?.filter(i => i > sequence).map(i => resetFields(`${uniqueKey ? `${uniqueKey}-` : ''}${i}`)).reduce((all, i) => ({ ...all, ...i }), {})
  }

  const handleDataSourceChange = ({ sequence, type }) => {
    const key = `${sequence}-${type}`
    form.setFieldsValue({ ...resetRelBelow(sequence), [`${key}-tableName`]: undefined, [`${key}-joinKey`]: undefined })
    setAnyCrossDataChange(true)
  }
  const handleTableChange = ({ sequence, type }) => {
    form.setFieldsValue({ ...resetRelBelow(sequence), [`${sequence}-${type}-joinKey`]: undefined })
    setAnyCrossDataChange(true)
  }

  const fields = [
    { key: 'dataSource', options: getDataSrcOpt, onChange: handleDataSourceChange },
    { key: 'tableName', options: getTableOpt, onChange: handleTableChange, dependency: 'dataSource' },
    { key: 'joinKey', options: getColumnOpt, dependency: 'tableName' }
  ]

  const handleAddRelationship = async () => {
    const validation = await validateCrossData(['dataSource', 'tableName']).catch(err => err)
    if (validation?.errorFields?.length) return
    setCount([...count, Math.max(...count) + 1])
  }

  const handleDelRelationship = sequence => {
    setCount(count.slice(0, -1))
    form.setFieldsValue(resetFields(sequence))
  }

  const handleStartRelate = async () => {
    setLoading(true)
    if (!hidePreview) {
      await validateCrossData()
      setTablePreviewStatus('loading')
      const reqBody = crossDataFormatSave(formVal, count)
      const res = await fetchCrossDataPreview(reqBody)
      if (res?.status !== 200) {
        setTablePreviewStatus(res?.data?.message)
        setLoading(false)
        return
      }
      const { columns, values } = res?.data?.data || {}
      if (!columns) setTablePreviewStatus('noData')
      setColumns(columns.map(i => ({ title: i.header, dataIndex: i.header })))
      setTablePreview(values?.map((i, idx) => ({ ...i, id: idx })))
      setTablePreviewStatus(res?.data?.status)
      handleFetchParams({})
    }
    await onRelate && onRelate()
    setLoading(false)
  }

  useEffect(() => {
    relationshipRef.current.scrollTop = relationshipRef.current.scrollHeight
  }, [count])

  const tablePreviewComponent = {
    blank: <></>,
    loading: <><Spin /> {ln_t('dashboard.loading')}...</>,
    noData: <Empty description={ln_t('dashboard.noData')} imageStyle={{ height: '70px' }} />,
    error: <>{ln_t('dashboard.error')}: {tablePreviewStatus}</>
  }

  return (
    <>
      <CrossDataSettingWrapper ref={relationshipRef}>
        {count?.map(num => {
          const sequence = uniqueKey ? `${uniqueKey}-${num}` : num
          return <div key={sequence}>
          <div style={{ fontWeight: 600, display: 'flex', height: '32px', alignItems: 'center' }}>
            {ln_t('dashboard.relationship')} {num}
            {count.length > 1 && num === count[count.length - 1] && !disabled?.includes(sequence) &&
              <Button type="link" icon={<DeleteOutlined />} style={{ color: 'var(--primary', marginLeft: 'auto' }} onClick={() => handleDelRelationship(sequence)}>
                {ln_t('util.delete')}
              </Button>}
          </div>
          <RelationshipWrapper style={useJoinLogic ? { paddingBottom: '4px' } : {}}>
            <RelationshipRow>
              {['parent', 'child'].map(type => <div key={`${sequence}-${type}`}>
                  {fields.map(field => {
                    const { key, options, dependency, onChange = () => {} } = field
                    const name = `${sequence}-${type}-${key}`
                    const opt = typeof options === 'function' ? options(sequence, type) : options
                    return <Form.Item key={name} {...defConfig(key, 'select')} name={name}>
                      <Select
                        showSearch
                        placeholder={defPlaceholder(key, 'select')}
                        options={opt}
                        optionFilterProp="children"
                        disabled={disabled?.includes(sequence) || (dependency ? !formVal[`${sequence}-${type}-${dependency}`] : false)}
                        onChange={() => onChange({ sequence, type }) }
                      />
                    </Form.Item>
                  })}
                </div>
              )}
            </RelationshipRow>
            {useJoinLogic && <Form.Item {...defConfig('logic', 'select')} name={`${sequence}-logic`}>
              <Select
                showSearch
                placeholder={defPlaceholder('logic', 'select')}
                options={[{ value: 'LEFT', label: 'Left Join' }, { value: 'RIGHT', label: 'Right Join' }, { value: 'OUTER', label: 'Outer Join' }]}
                optionFilterProp="children"
                disabled={disabled?.includes(sequence)}
              />
            </Form.Item>}
          </RelationshipWrapper>
        </div>
        })}
      </CrossDataSettingWrapper>
      {count.length < maxCount && !viewOnly &&
        <Button onClick={handleAddRelationship} icon={<PlusOutlined />} style={{ width: '100%' }} disabled={disabledActions}>
          {ln_t('util.add')} {ln_t('dashboard.relationship')}
        </Button>}
      <Button type='primary' style={{ display: 'flex', margin: '16px 0 4px auto' }} onClick={handleStartRelate} loading={loading} disabled={disabledActions}>
        {ln_t('dashboard.startRelate')}
      </Button>
      {hidePreview
        ? ''
        : <>
            <div>{ln_t('dashboard.dataPreview')}</div>
            {tablePreviewStatus === 'success'
              ? <MainTable
                  columns={columns}
                  rowKey="id"
                  apiFunc={dataPreviewApi}
                  params={params}
                  noPage
                />
              : <div style={{ color: 'rgba(0,0,0,.6)', border: '1px solid rgba(0,0,0,.3)', height: '120px', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                  {tablePreviewComponent[tablePreviewStatus] || tablePreviewComponent.error}
                </div>}
          </>}
    </>
  )
}

export default CrossDataSetting
